import React from "react";
import { BarChart2, Clock, Compass, Database, GitBranch, Home, Layout, Lock, Repeat, Settings, Share2, Zap } from "react-feather";
import { Bot } from "lucide-react";

export interface SidebarIconProps {
  size?: string | number;
  strokeWidth?: string | number;
}

export interface SidebarNavItem {
  id: string;
  to: string;
  label: string;
  icon: React.ComponentType<SidebarIconProps>;
  /** Whether this destination belongs in the compact contextual rail. */
  showInContextRail?: boolean;
  /**
   * Which group of the navigation drawer the item sits in; groups are divided by a rule.
   * Defaults to `main`.
   */
  section?: "main" | "manage";
  /**
   * Not built yet: shown disabled with a "Coming soon" tag and never navigated to.
   * No route, API or hook exists for it.
   */
  soon?: boolean;
  isSelected: (pathname: string) => boolean;
}

/** One 18px stroke icon set (react-feather, plus lucide Bot for Agents: feather has no robot). */
export const buildSidebarMainItems = (): SidebarNavItem[] => [
  {
    id: "home",
    to: "/home",
    label: "Home",
    icon: Home,
    showInContextRail: true,
    isSelected: (pathname) => pathname === "/home",
  },
  // The route is live (the page carries a preview banner), so the item is a real link everywhere.
  {
    id: "discover",
    to: "/discover",
    label: "Discover",
    icon: Compass,
    isSelected: (pathname) => pathname.startsWith("/discover"),
  },
  {
    id: "stories",
    to: "/stories",
    label: "Data stories",
    icon: BarChart2,
    showInContextRail: true,
    isSelected: (pathname) => pathname === "/stories",
  },
  // PENDING: PENDING-EXPLORE (a sample map; a story's Explore mode fills it from the story)
  {
    id: "explore",
    to: "/explore",
    label: "Explore",
    icon: Share2,
    isSelected: (pathname) => pathname.startsWith("/explore"),
  },
  {
    id: "data-connectors",
    to: "/data-connectors",
    label: "Data connectors",
    icon: Database,
    showInContextRail: true,
    isSelected: (pathname) => pathname.startsWith("/data-connectors"),
  },
  {
    id: "knowledge-graph",
    to: "/knowledge-graph",
    label: "Knowledge graph",
    icon: GitBranch,
    showInContextRail: true,
    isSelected: (pathname) => pathname.startsWith("/knowledge-graph"),
  },
  {
    id: "data-studio",
    to: "/data-studio",
    label: "Data studio",
    icon: Repeat,
    showInContextRail: true,
    isSelected: (pathname) => pathname.startsWith("/data-studio"),
  },
  {
    id: "agents",
    to: "/agents",
    label: "Agents",
    icon: Bot,
    isSelected: (pathname) => pathname.startsWith("/agents"),
  },
  {
    id: "automation",
    to: "/automation",
    label: "Automation",
    icon: Zap,
    isSelected: (pathname) => pathname === "/automation",
  },
  // PENDING: PENDING-NAV-ROUTES (config entry exists, no route in App.tsx)
  {
    id: "permissions",
    to: "/permissions",
    label: "Permissions",
    icon: Lock,
    showInContextRail: true,
    section: "manage",
    soon: true,
    isSelected: (pathname) => pathname === "/permissions",
  },
  // PENDING: PENDING-NAV-ROUTES (sample page)
  {
    id: "activity",
    to: "/activity",
    label: "Activity",
    icon: Clock,
    showInContextRail: true,
    section: "manage",
    isSelected: (pathname) => pathname === "/activity",
  },
];

export const SIDEBAR_SIMPLIFIED_EXTRA_ITEM: SidebarNavItem = {
  id: "templates",
  to: "/templates",
  label: "Templates",
  icon: Layout,
  section: "manage",
  // PENDING: PENDING-NAV-ROUTES (sample page)
  isSelected: (pathname) => pathname === "/templates",
};

export const SIDEBAR_SETTINGS_ITEM: SidebarNavItem = {
  id: "settings",
  to: "/settings",
  label: "Settings",
  icon: Settings,
  isSelected: (pathname) => pathname === "/settings",
};
